import {
  appCarViewBrowserModelResultSchema,
  appDocumentBrowserModelResultSchema,
  DOCUMENT_MODEL_QUALITY_LABELS,
  MODEL_QUALITY_LABELS,
  MODEL_VEHICLE_TYPE_LABELS,
  MODEL_VIEW_LABELS,
} from "@instacover/helpers"
import type { DocumentPredictedType } from "@instacover/instacar-database/browser"
import type { PredictionResult } from "@instacover/utils"
import { TypedTensor } from "onnxruntime-web"
import type { z } from "zod"

export { DOCUMENT_MODEL_QUALITY_LABELS, MODEL_QUALITY_LABELS, MODEL_VEHICLE_TYPE_LABELS, MODEL_VIEW_LABELS }
export type { PredictionResult }

export type ModelType = "car_view" | "documents"

export type OnnxModelResult = {
  output_view: TypedTensor<"float32">
  softmax_view: TypedTensor<"float32">
  output_vehicle_type: TypedTensor<"float32">
  softmax_vehicle_type: TypedTensor<"float32">
  sigmoid_quality: TypedTensor<"float32">
}

export type OnnxDocumentModelResult = {
  // just vectors
  output_document_type: TypedTensor<"float32">
  output_quality: TypedTensor<"float32">
  // vectors after softmax (normalized to 0-1 range)
  softmax_document_type: TypedTensor<"float32">
  softmax_quality: TypedTensor<"float32">
  // document type normalized to valid steps -> IdentityCardFrontCz2016 -> IdentityCardFront
  global_document_type: TypedTensor<"float32">
}

// Derived from the shared schema so the app, the upsertPhoto input and the getPhotos output cannot drift
export type CarViewPredictResponse = z.infer<typeof appCarViewBrowserModelResultSchema>

export type DocumentPredictResponse = z.infer<typeof appDocumentBrowserModelResultSchema>

export type Model<T> = {
  enabled: boolean
  loaded: boolean
  predict: (file: File) => Promise<T>
  warmUpTime?: number
  loadTime?: number
}

export const modelQualityLabel = {
  OK: "ok",
  BLUR: "blur",
  FROM_SCREEN: "from_screen",
  MISSING_LICENSE_PLATE: "missing_license_plate",
  SNOW: "snow",
  DARK: "dark",
  LIGHT: "light",
  FROST: "frost",
  LICENSE_PLATE_ERROR: "license_plate_error",
  NOT_REAL: "not_real",
  FAR_DISTANCE: "far_distance",
  OPEN_DOOR_OR_WINDOW: "open_door_or_window",
  BLACK_AND_WHITE: "black_and_white",
} as const

export const modelInvalidTypeLabel = {
  Other: "Other",
  WrongImage: "WrongImage",
  WrongAngle: "WrongAngle",
  WrongVin: "WrongVin",
  WrongWindscreen: "WrongWindscreen",
  WrongOdometer: "WrongOdometer",
} as const

export const documentModelInvalidTypeLabel = {
  WrongImage: "WrongImage",
  OtherDocument: "OtherDocument",
} as const

export const modelCombinedLabel = {
  VinOrOdometer: "VinOrOdometer",
} as const

export type ModelCombinedLabel = (typeof modelCombinedLabel)[keyof typeof modelCombinedLabel]

// Vehicle type labels - order must match ONNX model output
export type VehicleTypeLabel = (typeof MODEL_VEHICLE_TYPE_LABELS)[number]

export const DOCUMENT_MODEL_LABELS: DocumentPredictedType[] = [
  "VehicleRegistrationCertificateFront",
  "VehicleRegistrationCertificateBack",
  "RoadworthinessCertificateFront",
  "RoadworthinessCertificateBack",
  "Invoice",
  "AcceptanceProtocol",
  "OtherDocument",
  "IdentityCardFront",
  "IdentityCardBack",
  "DriversLicenseFront",
  "DriversLicenseBack",
  "AccidentStatementFront",
  "WrongImage",
] as const
